"use client"

import { type ReactNode } from "react"
import { motion, type Variants } from "motion/react"
import { cn } from "@/lib/utils"

interface AnimateInProps {
  children: ReactNode
  className?: string
  delay?: number
  /** Animation variant: fade-up (default), fade, fade-left, fade-right, scale */
  variant?: "fade-up" | "fade" | "fade-left" | "fade-right" | "scale" | "fade-down"
  /** Duration in seconds */
  duration?: number
  /** Trigger once or every time it enters viewport */
  once?: boolean
}

const variants: Record<string, Variants> = {
  "fade-up": {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0 },
  },
  "fade-down": {
    hidden: { opacity: 0, y: -16 },
    visible: { opacity: 1, y: 0 },
  },
  fade: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  },
  "fade-left": {
    hidden: { opacity: 0, x: -16 },
    visible: { opacity: 1, x: 0 },
  },
  "fade-right": {
    hidden: { opacity: 0, x: 16 },
    visible: { opacity: 1, x: 0 },
  },
  scale: {
    hidden: { opacity: 0, scale: 0.97 },
    visible: { opacity: 1, scale: 1 },
  },
}

export function AnimateIn({
  children,
  className,
  delay = 0,
  variant = "fade-up",
  duration = 0.5,
  once = true,
}: AnimateInProps) {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once, margin: "-50px" }}
      variants={variants[variant]}
      transition={{
        duration,
        delay: delay / 1000,
        ease: [0.16, 1, 0.3, 1],
      }}
      className={cn("h-full", className)}
    >
      {children}
    </motion.div>
  )
}

/** Stagger container — wrap children in AnimateIn or motion elements */
interface StaggerProps {
  children: ReactNode
  className?: string
  stagger?: number
  delay?: number
  once?: boolean
}

export function StaggerChildren({
  children,
  className,
  stagger = 0.08,
  delay = 0,
  once = true,
}: StaggerProps) {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once, margin: "-50px" }}
      transition={{
        staggerChildren: stagger,
        delayChildren: delay / 1000,
      }}
      className={cn(className)}
    >
      {children}
    </motion.div>
  )
}

/** Individual stagger item — use inside StaggerChildren */
interface StaggerItemProps {
  children: ReactNode
  className?: string
  variant?: "fade-up" | "fade" | "fade-left" | "fade-right" | "scale"
  duration?: number
}

export function StaggerItem({
  children,
  className,
  variant = "fade-up",
  duration = 0.5,
}: StaggerItemProps) {
  return (
    <motion.div
      variants={variants[variant]}
      transition={{
        duration,
        ease: [0.25, 0.4, 0, 1],
      }}
      className={cn(className)}
    >
      {children}
    </motion.div>
  )
}
